// The imperative shell around `getUserMedia` and `MediaRecorder`. It makes no
// decisions a child would notice — when to warn, when to cut off, what to
// keep are ./machine.ts's — it only reports what happened through callbacks,
// each at most once per capture. Not unit-tested: node and happy-dom have no
// MediaRecorder. It is verified by hand across the capture README's browser
// matrix, which is why it is kept this thin.
import { AudioMime, type CaptureMeta } from "../contract";
import { type StartFailure, startFailureFromError } from "./errors";
import type { Recording } from "./machine";

export interface CaptureCallbacks {
  // The recorder is running; `at` is on `now`'s clock.
  readonly onStarted: (at: number) => void;
  // getUserMedia refused, or the recorder could not be constructed/started.
  readonly onStartFailed: (error: StartFailure) => void;
  readonly onStopped: (recording: Recording) => void;
  // The recorder's `error` event: what was recorded is not trusted.
  readonly onFailed: () => void;
}

export interface CaptureDriver {
  // No-op unless recording. A driver-initiated stop (tab hidden, track
  // ended) is reported through `onStopped` with `endedBy: "track-ended"`.
  readonly stop: (endedBy: "user" | "max-duration") => void;
  // Unmount: stop the recorder and release the microphone; no callback
  // fires afterwards, including for a getUserMedia still pending.
  readonly dispose: () => void;
}

// Classroom noise is the reason for all three, and they are the only
// constraints: anything more (sample rate, channel count, a device id) is
// how a cheap Chromebook mic produces OverconstrainedError.
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// What the recorder reports after start() is the truth about the bytes; the
// requested candidate is only the fallback for a browser that reports
// nothing or something outside the contract's containers.
const resolveMime = (reported: string, requested: string): string =>
  AudioMime.safeParse(reported).success ? reported : requested;

// Must be called from a click/tap handler: getUserMedia is called
// synchronously here, so the permission prompt is tied to the child's tap
// and never appears unprompted.
export const startCapture = (
  requestedMime: string,
  callbacks: CaptureCallbacks,
  now: () => number = () => performance.now(),
): CaptureDriver => {
  let disposed = false;
  // Set once a terminal callback has fired, so exactly one ever does.
  let settled = false;
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let startedAt = 0;
  let stoppedAt: number | null = null;
  // Anything that stops the recorder without our asking — a track ending, the
  // UA — is `track-ended`; stop() overwrites it.
  let endedBy: CaptureMeta["endedBy"] = "track-ended";
  const chunks: Blob[] = [];

  const settle = (report: () => void): void => {
    if (settled || disposed) return;
    settled = true;
    report();
  };

  const requestStop = (by: CaptureMeta["endedBy"]): void => {
    if (recorder === null || recorder.state === "inactive") return;
    if (stoppedAt !== null) return;
    stoppedAt = now();
    endedBy = by;
    recorder.stop();
    // The mic light goes off now, not when the final data has been flushed.
    releaseMicrophone();
  };

  // A hidden tab keeps what was recorded rather than a hot mic in the
  // background.
  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") requestStop("track-ended");
  };
  const onTrackEnded = (): void => requestStop("track-ended");

  // Every exit from recording passes through here. Idempotent.
  function releaseMicrophone(): void {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    for (const track of stream?.getTracks() ?? []) {
      track.removeEventListener("ended", onTrackEnded);
      track.stop();
    }
  }

  const begin = (granted: MediaStream): void => {
    stream = granted;
    if (disposed) {
      releaseMicrophone();
      return;
    }
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(granted, { mimeType: requestedMime });
    } catch {
      // NotSupportedError for a type isTypeSupported accepted: a browser bug,
      // not something an adult can fix.
      releaseMicrophone();
      settle(() => callbacks.onStartFailed({ kind: "recorder-failed" }));
      return;
    }
    rec.addEventListener("dataavailable", (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    });
    rec.addEventListener("error", () => {
      releaseMicrophone();
      settle(callbacks.onFailed);
    });
    rec.addEventListener("stop", () => {
      releaseMicrophone();
      settle(() => {
        const mime = resolveMime(rec.mimeType, requestedMime);
        const blob = new Blob(chunks, { type: mime });
        callbacks.onStopped({
          meta: {
            mime,
            durationMs: Math.max(
              0,
              Math.round((stoppedAt ?? now()) - startedAt),
            ),
            bytes: blob.size,
            endedBy,
          },
          blob,
        });
      });
    });
    try {
      rec.start();
    } catch {
      releaseMicrophone();
      settle(() => callbacks.onStartFailed({ kind: "recorder-failed" }));
      return;
    }
    recorder = rec;
    startedAt = now();
    document.addEventListener("visibilitychange", onVisibilityChange);
    for (const track of granted.getAudioTracks())
      track.addEventListener("ended", onTrackEnded);
    callbacks.onStarted(startedAt);
  };

  navigator.mediaDevices
    .getUserMedia({ audio: AUDIO_CONSTRAINTS })
    .then(begin, (error: unknown) => {
      settle(() => callbacks.onStartFailed(startFailureFromError(error)));
    });

  return {
    stop: requestStop,
    dispose: () => {
      disposed = true;
      if (recorder !== null && recorder.state !== "inactive") recorder.stop();
      releaseMicrophone();
    },
  };
};
